import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Autocomplete, Box, TextField } from '@mui/material';
import { ActionBarStyled, SelectStyled } from '../../../components/PageAction/styles';
import { FlexboxStyled } from '../../../styles/IndexStyles';
import { FlexStack, InputWrapper } from '../../../styles/EditDeleteStyles';
import { LabelStyled } from '../../../components/InputSelect/styles';
import { FlexColumnStyled, FlexRowStyled } from '../../../styles/Flexbox';
import { BannerImageSelect } from './Shared/BannerImageSelect';
import { TextArea } from '../../../components/InputTextArea';
import InputText from '../../../components/InputText';
import InputCheckbox from '../../../components/InputCheckbox/Index';
import { useProject } from '../../../hooks';
import { colours } from '../../../styles/theme';
import { showAlertModal } from './Shared/alertModal';
import { Sending } from './Shared/Sending';

const studyLabel = (study: any) => `${study?.id} - ${study.project_name}`;

type StudyPromotionProps = {
  onChange: (data: any) => void;
  error?: any;
  loading: boolean;
};

export function StudyPromotion({ onChange, error, loading }: StudyPromotionProps): JSX.Element {
  const { projects, getProjects } = useProject();
  const [studyType, setStudyType] = useState<string>('study');
  const [region, setRegion] = useState<string>('1'); // value is region id
  const [ageFrom, setAgeFrom] = useState<string>('');
  const ageFromFormKey = 'bulk_reminder_ageFrom';
  const [ageTo, setAgeTo] = useState<string>('');
  const ageToFormKey = 'bulk_reminder_ageTo';
  const [body, setBody] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const bodyFormKey = 'bulk_reminder_body';
  const [parentGuardian, setParentGuardian] = useState<boolean>(false);
  const [study, setStudy] = useState<any>({});
  const [bannerImage, setBannerImage] = useState<File>();
  const [bannerImageName, setBannerImageName] = useState<string>('');

  const handleSettingStudy = async (id: any, result: any) => {
    if (Object.keys(study).length < 3) {
      setStudy({ ...study, [id]: result });
    } else {
      await showAlertModal('You can only add up to 3 studies');
    }
  };

  React.useEffect(() => {
    getProjects();
  }, []);

  React.useEffect(() => {
    setBannerImageName(bannerImage?.name || '');
  }, [bannerImage]);

  React.useEffect(() => {
    onChange({
      studyType,
      region,
      parentGuardian,
      ageFrom,
      ageTo,
      subject,
      body,
      bannerImage,
      bannerImageName,
      study,
    });
  }, [
    studyType,
    region,
    parentGuardian,
    ageFrom,
    ageTo,
    subject,
    body,
    bannerImage,
    bannerImageName,
    study,
    onChange,
  ]);

  if (loading) return <Sending />;

  return (
    <>
      <InputWrapper>
        <FlexColumnStyled>
          <FlexRowStyled>
            <ActionBarStyled>
              <FlexboxStyled style={{ marginBottom: 0 }}>
                <LabelStyled htmlFor="action" style={{ color: 'black' }}>
                  Email Recipients
                </LabelStyled>
                <SelectStyled
                  style={{ minWidth: '245px' }}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLSelectElement>) => {
                    setStudyType(currentTarget?.value);
                  }}
                  value={studyType}
                >
                  {[
                    {
                      value: 'study',
                      label: 'General Research Study',
                    },
                    {
                      value: 'ppo',
                      label: 'Patient Partner Research Study',
                    },
                    {
                      value: 'all',
                      label: 'All Research Studies',
                    },
                  ].map(({ label, value }, i) => (
                    <option value={value} key={i}>
                      {label}
                    </option>
                  ))}
                </SelectStyled>
              </FlexboxStyled>
              <FlexboxStyled style={{ marginBottom: 0 }}>
                <SelectStyled
                  style={{ minWidth: '245px' }}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLSelectElement>) => {
                    setRegion(currentTarget?.value);
                  }}
                  value={region}
                >
                  {[
                    {
                      value: '1',
                      label: 'Vancouver Island & Coast',
                    },
                    {
                      value: '2',
                      label: 'Lower Mainland',
                    },
                    {
                      value: '3',
                      label: 'Thompson - Okanagan',
                    },
                    {
                      value: '4',
                      label: 'Kootenay',
                    },
                    {
                      value: '5',
                      label: 'Cariboo',
                    },
                    {
                      value: '6',
                      label: 'North Coast and Nechako',
                    },
                    {
                      value: '7',
                      label: 'Northeast',
                    },
                    {
                      value: '0',
                      label: 'Online',
                    },
                  ].map(({ label, value }, i) => (
                    <option value={value} key={i}>
                      {label}
                    </option>
                  ))}
                </SelectStyled>
              </FlexboxStyled>
            </ActionBarStyled>
          </FlexRowStyled>
          <InputWrapper style={{ borderBottom: '0px', paddingLeft: '0px', paddingBottom: '0px' }}>
            <FlexRowStyled style={{ justifyContent: 'flex-start' }}>
              <InputText
                style={{ width: '55px' }}
                labelStyle={{ marginLeft: '170px', width: '110px', fontWeight: 'normal' }}
                textWrapperStyle={{ width: '55px' }}
                id={ageFromFormKey}
                keyName={ageFromFormKey}
                label="Age Range from:"
                error={error?.ageFrom}
                placeholder="Age"
                inputValue={ageFrom}
                handleHandle={setAgeFrom}
                padding={false}
              />
              <InputText
                style={{ width: '55px' }}
                labelStyle={{ marginLeft: '5px', width: '20px', fontWeight: 'normal' }}
                textWrapperStyle={{ width: '55px' }}
                id={ageToFormKey}
                keyName={ageToFormKey}
                label="to"
                error={error?.ageTo}
                placeholder="Age"
                inputValue={ageTo}
                handleHandle={setAgeTo}
                padding={false}
              />
              <InputCheckbox
                style={{ marginLeft: '9px' }}
                postLabal="Parent/Gardian"
                checkedValue={parentGuardian}
                handleChange={setParentGuardian}
              />
            </FlexRowStyled>
          </InputWrapper>
        </FlexColumnStyled>
      </InputWrapper>
      <InputWrapper>
        <InputText
          id="subjectFormKey"
          keyName="subjectFormKey"
          label="Subject"
          error={error?.subject}
          placeholder="Email subject for platform-wide notification"
          inputValue={subject}
          handleHandle={setSubject}
          padding={false}
          theme="form"
        />
      </InputWrapper>
      <BannerImageSelect
        handleChange={(value: File) => setBannerImage(value)}
        fileName={bannerImage?.name}
        error={error?.bannerImageName}
      />
      <InputWrapper>
        <TextArea
          id={bodyFormKey}
          keyName={bodyFormKey}
          label="Body Text"
          error={error?.body}
          placeholder="Add your body text here"
          textValue={body}
          handleHandle={setBody}
        />
      </InputWrapper>
      <InputWrapper>
        <FlexRowStyled style={{ marginTop: '4px', marginBottom: '2px' }}>
          <div
            style={{
              padding: '0px 0px 8px 0px',
              fontFamily:
                'Roboto, "Lucida Grande", "DejaVu Sans", "Bitstream Vera Sans", Verdana, Arial, sans-serif',
              fontSize: '0.8125rem',
              fontWeight: 'bold',
              width: '170px',
            }}
          >
            Select Studies (max 3)
          </div>
          <div>
            <FlexStack style={{ width: '100%' }}>
              <Autocomplete
                id="study-lookup"
                sx={{ width: 500 }}
                options={projects}
                autoHighlight
                getOptionLabel={(option) => studyLabel(option)}
                onChange={(e, optionValue) =>
                  !!optionValue && handleSettingStudy(optionValue?.id, optionValue)
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {studyLabel(option)}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for a Study..."
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                      },
                    }}
                  />
                )}
              />
              <div
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  marginTop: '4px',
                  marginLeft: '100px',
                  color: 'rgb(186, 33, 33)',
                  fontFamily:
                    'Roboto, "Lucida Grande", "DejaVu Sans", "Bitstream Vera Sans", Verdana, Arial, sans-serif',
                }}
              >
                {error?.study}
              </div>
            </FlexStack>
            <div>
              {Object.keys(study).length > 0 && (
                <div>
                  {Object.keys(study).map((id) => (
                    <div key={id} style={{ marginTop: '10px' }}>
                      <FlexRowStyled>
                        <span
                          style={{
                            fontFamily:
                              'Roboto, "Lucida Grande", "DejaVu Sans", "Bitstream Vera Sans", Verdana, Arial, sans-serif',
                          }}
                        >
                          {study[id]?.id} - {study[id].project_name}
                        </span>
                        <FontAwesomeIcon
                          icon={faTrash}
                          color={colours.colour18}
                          style={{
                            marginLeft: '1em',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            userSelect: 'none',
                            fontSize: '14px',
                            paddingTop: '1px',
                          }}
                          role="button"
                          onClick={() =>
                            setStudy(() => {
                              const { [id]: _, ...rest } = study;
                              return rest;
                            })
                          }
                        />
                      </FlexRowStyled>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </FlexRowStyled>
      </InputWrapper>
    </>
  );
}
